import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ulid } from 'ulid';
import API from '../../../Services/API';
import { useSession } from '../../../Components/Session';
import { useToasts } from '../../../Components/Toasts';
import { ChallengeTypes } from '../../../Data/Users';
import { isLocal } from '../../../Services/helpers';
import EmailChallenge from './EmailChallenge';
import PasswordChallenge from './PasswordChallenge';
import TrustDeviceChallenge from './TrustDeviceChallenge';
import MfaChallenge from './MfaChallenge';
import PasskeyChallenge from './PasskeyChallenge';
import RegisterChallenge from './RegisterChallenge';
import Problems from './Problems';
import { PageLoading } from '../../../Components/Loading';
import Card from '../../../Components/Containers/Card';
import Button from '../../../Components/Button/Button';
import ButtonBar from '../../../Components/Button/ButtonBar';
import UpdatePrompt from '../../../Components/UpdatePrompt';

const Challenges = () => {
    const {setUser, setBusiness, fetchDetails, deviceId} = useSession();
    const navigate = useNavigate();
    const {showError} = useToasts();
    const [sessionId, setSessionId] = useState(() => ulid());
    const [challenge, setChallenge] = useState(null);
    const [isStarting, setIsStarting] = useState(true);
    const [email, setEmail] = useState(null);
    const [showProblems, setShowProblems] = useState(false);
    const [expiresAt, setExpiresAt] = useState(null);
    const [isExpired, setIsExpired] = useState(false);
    const [registerMode, setRegisterMode] = useState(false);
    const [registerEmail, setRegisterEmail] = useState(null);

    const startChallenge = async () => {
        const newSessionId = ulid();
        setSessionId(newSessionId);
        setIsStarting(true);
        setEmail(null);
        setIsExpired(false);
        setRegisterMode(false);
        setRegisterEmail(null);
        try {
            const payload = {
                sessionId: newSessionId,
                deviceId,
            };

            if (isLocal()) {
                payload._ja4Fingerprint = 't13d0016h2_8daaf6152771_0d0000000000';
                payload._city = 'London';
            }

            const response = await API.post('/auth/challenge/start', payload);
            setChallenge(response.challenge);
            setIsExpired(false);
            setExpiresAt(response.expiresAt ? new Date(response.expiresAt) : null);
        } catch (err) {
            showError(API.parseErrorResponse(err) ?? 'Failed to start authentication');
        } finally {
            setIsStarting(false);
        }
    };

    const handleChallengeResponse = async (data) => {
        if (!sessionId || !challenge) {
            return;
        }

        try {
            const payload = {
                sessionId,
                type: challenge.type,
                data,
            };

            if (isLocal()) {
                payload._ja4Fingerprint = 't13d0016h2_8daaf6152771_0d0000000000';
                payload._city = 'London';
            }

            const response = await API.post('/auth/challenge/respond', payload);

            if (data?.email && challenge.type === ChallengeTypes.EMAIL) {
                setEmail(data.email);
            }

            if (response.complete) {
                if (response.token) {
                    API.setToken(response.token);
                    setUser(response.user);
                    setBusiness(response.business);
                    fetchDetails();
                    const redirectAfterLogin = window.sessionStorage.getItem('redirectAfterLogin');
                    window.sessionStorage.removeItem('redirectAfterLogin');
                    navigate(redirectAfterLogin || '/');
                } else {
                    showError('Authentication completed but no token received');
                }
            } else if (response.challenge) {
                setChallenge(response.challenge);
                setExpiresAt(response.expiresAt ? new Date(response.expiresAt) : null);
            } else {
                showError('Unexpected response from server');
            }
        } catch (err) {
            if (challenge.type === ChallengeTypes.EMAIL && data?.email) {
                setRegisterMode(true);
                setRegisterEmail(data.email);
            } else {
                showError(API.parseErrorResponse(err) ?? 'Authentication failed');
            }
        }
    };

    useEffect(() => {
        startChallenge();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!expiresAt) {
            return;
        }

        const checkExpiration = () => {
            const now = new Date();
            if (now >= expiresAt) {
                setIsExpired(true);
            }
        };

        checkExpiration();

        const interval = setInterval(checkExpiration, 5000);

        return () => clearInterval(interval);
    }, [expiresAt]);

    const renderChallenge = () => {
        if (registerMode) {
            return (
                <RegisterChallenge
                    email={registerEmail}
                    onBack={() => {
                        setRegisterMode(false);
                        setRegisterEmail(null);
                    }}
                    onSuccess={() => {
                        const redirectAfterLogin = window.sessionStorage.getItem('redirectAfterLogin');
                        window.sessionStorage.removeItem('redirectAfterLogin');
                        navigate(redirectAfterLogin || '/');
                    }}
                />
            );
        }

        if (isStarting || !challenge) {
            return <PageLoading />;
        }

        if (isExpired) {
            return (
                <div className="text-center py-8">
                    <p className="text-muted mb-4">Your login session has expired.</p>
                    <Button
                        label="Start Again"
                        theme="primary"
                        onClick={startChallenge}
                    />
                </div>
            );
        }

        switch (challenge.type) {
            case ChallengeTypes.EMAIL:
                return <EmailChallenge onSubmit={handleChallengeResponse} />;
            case ChallengeTypes.PASSWORD:
                return <PasswordChallenge onSubmit={handleChallengeResponse} />;
            case ChallengeTypes.TRUST_DEVICE:
                return <TrustDeviceChallenge onSubmit={handleChallengeResponse} />;
            case ChallengeTypes.MFA:
                return <MfaChallenge onSubmit={handleChallengeResponse} />;
            case ChallengeTypes.PASSKEY:
                return <PasskeyChallenge onSubmit={handleChallengeResponse} challenge={challenge} />;
            default:
                return <div>Unknown challenge type: {challenge.type}</div>;
        }
    };

    if (showProblems) {
        return <Problems onBack={() => setShowProblems(false)} />;
    }

    return <>
        <img src="/logo-name-horizontal.png"
            alt="getSoapy Logo"
            width="888"
            height="183"
            className="w-[75%] h-auto mx-auto mb-8"
        />

        <Card>
            <ButtonBar
                className="mb-4"
                buttons={[
                    {label: 'Login', selected: !registerMode, onClick: () => setRegisterMode(false)},
                    {label: 'Register', selected: registerMode, onClick: () => setRegisterMode(true)},
                ]}
            />
            {renderChallenge()}
        </Card>

        <div className="mt-5 flex flex-col gap-y-2 text-sm text-center">
            {email && <button
                onClick={startChallenge}
                className="cursor-pointer text-muted hover:text-brand"
                type="button"
            >
                Not {email}?
            </button>}
            <button
                onClick={() => setShowProblems(true)}
                className="cursor-pointer text-muted hover:text-brand"
                type="button"
            >
                Problems logging in?
            </button>
        </div>

        <UpdatePrompt />
    </>;
};

export default Challenges;
